import React, { useEffect, useRef, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import activityTracker from '../../utils/activityTracker';
import usePushNotifications from '../../hooks/usePushNotifications';
import { AuthContext } from '../../context/AuthContext';

/**
 * ActivityRouter component
 * Router-level component that tracks browsing activity and page views
 * Similar to AnalyticsRouter but for our internal activity tracking
 */
const ActivityRouter = () => {
  const location = useLocation();
  const { pathname, search } = location;
  const lastTrackedPath = useRef(null);
  const { deviceToken } = usePushNotifications(); // Get FCM device token
  const { authState } = useContext(AuthContext); // Get auth state to check admin status

  // Update activity tracker with device token when available
  useEffect(() => {
    if (deviceToken) {
      activityTracker.setDeviceToken(deviceToken);
    }
  }, [deviceToken]);

  // Update admin status to disable tracking for admin users
  useEffect(() => {
    const isAdmin = authState.isAuthenticated && authState.user?.is_admin;
    activityTracker.setAdminStatus(isAdmin);
  }, [authState.isAuthenticated, authState.user]);

  // Effect runs on route change
  useEffect(() => {
    const fullPath = pathname + search;

    // Only track if this is a different path than last tracked
    // This prevents duplicate tracking when component remounts (React StrictMode)
    if (lastTrackedPath.current === fullPath) {
      return;
    }

    // Track page view on route change
    const pageTitle = document.title;
    activityTracker.trackPageView(pageTitle, fullPath);

    // Remember this path to prevent duplicates
    lastTrackedPath.current = fullPath;
  }, [pathname, search]); // Re-run when route changes

  // This component doesn't render anything
  return null;
};

export default ActivityRouter;
